const MODE_KEY = "auth_experience_mode";
const SWITCH_ENABLED_KEY = "auth_experience_switch_enabled";
const URL_PARAM = "enableAuthExperienceSwitch";

/** @type {ReadonlySet<'legacy' | 'discovery'>} */
const VALID_STORAGE = new Set(["legacy", "discovery"]);

/** @type {ReadonlySet<'classic' | 'progressive'>} */
const VALID_APP = new Set(["classic", "progressive"]);

/** Storage value → in-app experience */
const STORAGE_TO_APP = {
  legacy: "classic",
  discovery: "progressive",
};

/** In-app experience → storage value */
const APP_TO_STORAGE = {
  classic: "legacy",
  progressive: "discovery",
};

export const DEFAULT_LOGIN_EXPERIENCE = "classic";

/**
 * @returns {'classic' | 'progressive'}
 */
export function readLoginExperience() {
  try {
    const raw = localStorage.getItem(MODE_KEY);
    if (VALID_STORAGE.has(raw)) {
      return STORAGE_TO_APP[raw];
    }
  } catch (_err) {
    /* ignore */
  }
  return DEFAULT_LOGIN_EXPERIENCE;
}

/**
 * @param {'classic' | 'progressive'} mode
 * @returns {'classic' | 'progressive'}
 */
export function writeLoginExperience(mode) {
  const value = VALID_APP.has(mode) ? mode : DEFAULT_LOGIN_EXPERIENCE;
  try {
    localStorage.setItem(MODE_KEY, APP_TO_STORAGE[value]);
  } catch (_err) {
    /* ignore */
  }
  return value;
}

/**
 * @returns {boolean}
 */
export function isAuthExperienceSwitchEnabled() {
  try {
    return localStorage.getItem(SWITCH_ENABLED_KEY) === "true";
  } catch (_err) {
    return false;
  }
}

/**
 * @param {boolean} enabled
 */
export function setAuthExperienceSwitchEnabled(enabled) {
  try {
    localStorage.setItem(SWITCH_ENABLED_KEY, enabled ? "true" : "false");
  } catch (_err) {
    /* ignore */
  }
}

/**
 * Apply `enableAuthExperienceSwitch` query param when present.
 * - `true`: persist switch enabled
 * - `false`: persist switch disabled and reset experience to classic
 * - absent: leave storage unchanged
 * @param {string} [search]
 * @returns {boolean} whether the experience switcher should be shown
 */
export function applyAuthExperienceSwitchUrlParam(
  search = typeof window !== "undefined" ? window.location.search : ""
) {
  const params = new URLSearchParams(search);
  const param = params.get(URL_PARAM);
  if (param === "true") {
    setAuthExperienceSwitchEnabled(true);
    return true;
  }
  if (param === "false") {
    setAuthExperienceSwitchEnabled(false);
    writeLoginExperience(DEFAULT_LOGIN_EXPERIENCE);
    if (typeof document !== "undefined") {
      document.documentElement.dataset.loginExperience =
        DEFAULT_LOGIN_EXPERIENCE;
    }
    return false;
  }
  return isAuthExperienceSwitchEnabled();
}

/**
 * Prefer bootstrap dataset set by index.html (avoids flicker); fall back to localStorage.
 * @returns {'classic' | 'progressive'}
 */
export function getBootstrapExperience() {
  if (typeof document !== "undefined") {
    const fromDataset = document.documentElement?.dataset?.loginExperience;
    if (VALID_APP.has(fromDataset)) {
      return fromDataset;
    }
  }
  return readLoginExperience();
}
